import { Building2, Check, ChevronsUpDown, LogOut, Moon, Sun, SunMoon } from 'lucide-react';
import { useSidebarHold } from './sidebar-hold.ts';
import { useSession } from '../../lib/session.tsx';
import { THEMES, useTheme, type Theme } from '../../lib/settings.ts';
import { cn } from '../../lib/utils.ts';
import { Badge } from '../ui/badge.tsx';
import { Button } from '../ui/button.tsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu.tsx';

/**
 * The account controls, where Leve keeps them (TS-1681): the organisation switcher at the top of
 * the sidebar, the theme toggle and the profile row (avatar, name, email; the menu signs out) at
 * its foot. Styled with the design system's v2 Sidebar tokens.
 */

const THEME_LABEL: Record<Theme, string> = { system: 'System', light: 'Light', dark: 'Dark' };

/** First letters of the first two words of a name ("Ankush Mahanty" → "AM"). */
function initialsOf(name: string): string {
  const parts = name.split(/[\s@._-]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?';
}

/** Organisation switcher: a choice among the user's own memberships; the API re-scopes everything else. */
export function OrgSwitcher({ collapsed }: { collapsed: boolean }) {
  const hold = useSidebarHold();
  const session = useSession();
  const { organisation, memberships } = session.me;
  const row = cn(
    'flex h-11 w-full min-w-0 items-center gap-2 rounded-xl border border-(--v2-sidebar-border) bg-(--v2-sidebar-bg) text-sm font-medium text-(--v2-sidebar-text-primary)',
    collapsed ? 'justify-center px-0' : 'px-3',
  );
  const name = (
    <span
      className={cn('min-w-0 flex-1 truncate text-left', collapsed && 'sr-only')}
      data-testid="org-name"
    >
      {organisation.name}
    </span>
  );
  if (memberships.length <= 1)
    return (
      <div className={row}>
        <Building2 className="text-brand size-4 shrink-0" aria-hidden />
        {name}
      </div>
    );
  return (
    <DropdownMenu onOpenChange={hold}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(row, 'hover:bg-(--v2-sidebar-row-hover-bg) transition-colors')}
          disabled={session.switching}
          data-testid="org-switcher"
        >
          <Building2 className="text-brand size-4 shrink-0" aria-hidden />
          {name}
          {!collapsed && (
            <ChevronsUpDown className="text-muted-foreground size-4 shrink-0" aria-hidden />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel>Switch organisation</DropdownMenuLabel>
        {memberships.map((m) => (
          <DropdownMenuItem
            key={m.id}
            onSelect={() => void session.switchOrganisation(m.id)}
            className="justify-between"
          >
            <span className="flex flex-col">
              <span>{m.name}</span>
              <span className="text-muted-foreground text-xs">{m.role}</span>
            </span>
            {m.id === organisation.id && <Check className="size-4" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function ThemeMenu() {
  const hold = useSidebarHold();
  const [theme, setTheme] = useTheme();
  const Icon = theme === 'dark' ? Moon : theme === 'light' ? Sun : SunMoon;
  return (
    <DropdownMenu onOpenChange={hold}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="text-muted-foreground hover:text-foreground size-9 rounded-full"
          aria-label={`Theme: ${THEME_LABEL[theme]}`}
        >
          <Icon />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="top">
        <DropdownMenuLabel>Appearance</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={theme} onValueChange={(v) => setTheme(v as Theme)}>
          {THEMES.map((t) => (
            <DropdownMenuRadioItem key={t} value={t}>
              {THEME_LABEL[t]}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** The profile row at the foot of the sidebar; its menu holds the role and Sign out. */
export function ProfileMenu({ collapsed }: { collapsed: boolean }) {
  const hold = useSidebarHold();
  const session = useSession();
  const { user, organisation } = session.me;
  return (
    <DropdownMenu onOpenChange={hold}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex w-full min-w-0 items-center gap-3 rounded-lg p-1.5 text-left transition-colors hover:bg-(--v2-sidebar-row-hover-bg)',
            collapsed && 'justify-center',
          )}
          aria-label="Account menu"
          data-testid="user-menu"
        >
          <span className="bg-brand grid size-9 shrink-0 place-items-center rounded-full text-xs font-semibold text-white">
            {initialsOf(user.name)}
          </span>
          {!collapsed && (
            <span className="flex min-w-0 flex-col leading-tight">
              <span className="truncate text-sm font-semibold text-(--v2-sidebar-text-primary)">
                {user.name}
              </span>
              <span className="truncate text-xs text-(--v2-sidebar-muted-foreground)">
                {user.email}
              </span>
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="top" className="w-64">
        <DropdownMenuLabel className="flex flex-col gap-1">
          <span className="text-foreground truncate text-sm font-medium">{user.email}</span>
          <span className="flex items-center gap-2">
            <Badge variant="secondary">{organisation.role}</Badge>
            {user.status !== 'active' && <Badge variant="warning">{user.status}</Badge>}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => session.signOut()} data-testid="sign-out">
          <LogOut /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
